import React, { useMemo, useEffect } from 'react'
import PropTypes from 'helpers/proptypes'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { getUser, getSortedLanguages } from 'redux/entities/selectors'
import { fetchUser } from 'redux/entities/actions'
import { Header, List, Icon } from 'semantic-ui-react'
import { UserCard } from 'components/cards/UserCard'

export function UserSection({ hostApplication: { user, host } }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const applicant = useSelector((state) => getUser(state, { id: user.id }))
  const spokenLanguages = useSelector(getSortedLanguages).filter(({ id, title }) => ({
    id,
    title,
  }))

  const socialLinks = useMemo(() => {
    const onlineProfiles = host?.get('online_profiles')
    if (!onlineProfiles) return []
    return [
      { url: onlineProfiles?.get('facebook'), icon: 'facebook' },
      { url: onlineProfiles?.get('instagram'), icon: 'instagram' },
      { url: onlineProfiles?.get('twitter'), icon: 'twitter' },
      { url: onlineProfiles?.get('tripAdvisor'), icon: 'tripadvisor' },
      { url: onlineProfiles?.get('website'), icon: 'linkify' },
      { url: onlineProfiles?.get('other'), icon: 'linkify' },
    ].filter(({ url }) => !!url)
  }, [host])

  useEffect(() => {
    if (!user.locality && !user.country) dispatch(fetchUser(user.id))
  }, [dispatch, user.country, user.id, user.locality])

  if (!user) return null

  return (
    <UserCard user={applicant} brandName={host?.getIn(['motivation', 'business_name'])} showUserDetails={false}>
      {spokenLanguages.length > 0 && (
        <List>
          <List.Item>
            <Icon name='language' />
            <List.Content>
              {spokenLanguages.map(({ id, title }) => (
                <List.Item key={id}>{title}</List.Item>
              ))}
            </List.Content>
          </List.Item>
        </List>
      )}
      {socialLinks.length > 0 && (
        <React.Fragment>
          <Header size='small'>{t('HostApplications::Summary::Social Media Links')}</Header>
          <List>
            {socialLinks.map(({ url, icon }) => (
              <List.Item key={url}>
                <Icon name={icon} />
                <List.Content verticalAlign='middle'>
                  <a
                    href={url}
                    style={{ display: 'inline-block', wordBreak: 'break-word' }}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {url}
                  </a>
                </List.Content>
              </List.Item>
            ))}
          </List>
        </React.Fragment>
      )}
    </UserCard>
  )
}

UserSection.propTypes = {
  hostApplication: PropTypes.shape({
    place: PropTypes.immutable.map,
    host: PropTypes.immutable.map,
    user: PropTypes.shape({
      id: PropTypes.number.isRequired,
      locality: PropTypes.string,
      country: PropTypes.string,
    }),
  }),
}

UserSection.defaultProps = {
  hostApplication: {
    place: undefined,
    host: undefined,
    user: undefined,
  },
}
